import { informationService } from '../services'

const state = {
  informations: [],
  information: null,
  itemsPerPage: 25,
  pagination: null,
  statuses: null,
  initialStatus: null,
  datatableParams: null,
  files: [],
  history: [],
  file: null
}

const getters = {
  getInformations (state) {
    return state.informations
  },
  pagination (state) {
    return state.pagination
  },
  getItemsPerPage (state) {
    return state.itemsPerPage
  },
  getStatusTranslation (state) {
    return status => state.statuses[status].label
  },
  getInitialStatus (state) {
    return state.initialStatus
  },
  findAllFiles (state) {
    return state.files
  },
  findFile (state) {
    return state.file
  },
  findHistory (state) {
    return state.history
  }
}

const mutations = {
  create (state, item) {
    const foundAt = state.informations.findIndex(inf => inf.id === item.id)
    if (foundAt === -1) {
      state.informations.push(item)
    }
  },
  update (state, { id, item }) {
    const foundAt = state.informations.findIndex(inf => inf.id === id)
    state.informations.splice(foundAt, 1, item)
  },
  delete (state, id) {
    const index = state.informations.findIndex(inf => inf.id === id)

    if (index !== -1) {
      state.informations.splice(index, 1)
    }

    if (state.pagination) {
      state.pagination.totalItems = state.informations.length
    }
  },
  getAll (state, informations) {
    state.informations = informations
  },
  find (state, information) {
    if (information.origin === null) {
      if (information.otherOrigin === null) {
        information.origin = { id: null, label: '' }
      } else {
        information.origin = { id: 'other', label: 'Autre' }
      }
    }

    if (information.recipient === null) {
      if (information.otherRecipient === null) {
        information.recipient = { id: null, label: '' }
      } else {
        information.recipient = { id: 'other', label: 'Autre' }
      }
    }

    state.initialStatus = information.status
    state.information = information
  },
  getEventInformations (state, informations) {
    state.informations = informations
  },
  setInformationsItemsPerPage (state, informationsItemsPerPage) {
    state.itemsPerPage = informationsItemsPerPage
  },
  pagination (state, pagination) {
    state.pagination = pagination
  },
  resetCurrentInformation (state) {
    state.information = null
  },
  persistDatatableParams (state, params) {
    state.datatableParams = params
  },
  findAllFiles (state, files) {
    state.files = files
  },
  removeFile (state, fileId) {
    const foundAt = state.files.findIndex(file => file.id === fileId)

    if (foundAt > -1) {
      state.files.splice(foundAt, 1)
    }
  },
  addFile (state, file) {
    state.files.push(file)
  },
  findFile (state, fileId) {
    state.file = state.information.attachments.find(file => file.id === fileId)
  },
  findHistory (state, history) {
    state.history = history
  }
}

const actions = {
  exportInformation (context, { eventId, informationId, mapUrl, format }) {
    context.dispatch('loader/startLoading', 'information/export', { root: true })
    return informationService.exportInformation(eventId, informationId, mapUrl, format)
      .catch(() => {
        context.dispatch('alert/error', `Echec de l'export de l'information ${informationId}.`, { root: true })
      })
      .finally(() => {
        context.dispatch('loader/stopLoading', 'information/export', { root: true })
      })
  },
  findAll (context) {
    return informationService.findAll()
      .then(response => {
        context.commit('getAll', response.data)
        return response
      })
  },
  find (context, { eventId, informationId }) {
    return informationService.find({ eventId, informationId })
      .then(response => {
        context.commit('find', response.data)
        return response
      })
  },
  findEventInformations (context, params) {
    context.commit('persistDatatableParams', params)
    return informationService.findEventInformations(params)
      .then(response => {
        context.commit('getEventInformations', response.data.items)
        context.commit('pagination', response.data.pagination)
        return response
      })
  },
  setInformationsItemsPerPage (context, informationsItemsPerPage) {
    context.commit('setInformationsItemsPerPage', informationsItemsPerPage)
  },
  delete (context, { informationId, eventId }) {
    return informationService.deleteInformation(eventId, informationId)
      .then(response => {
        context.commit('delete', informationId)
        return response
      })
  },
  resetCurrentInformation (context) {
    context.commit('resetCurrentInformation')
  },
  create (context, information) {
    context.dispatch('loader/startLoading', 'information/create', { root: true })
    return informationService.create(information)
      .then(response => {
        context.dispatch('information/findEventInformations', context.state.datatableParams, { root: true })
        context.dispatch('alert/success', `Création réussie de l'information ${response.data.label}.`, { root: true })
        return response.data
      })
      .catch(() => {
        context.dispatch('alert/error', `Echec de la création de l'information ${information.label}.`, { root: true })
      })
      .finally(() => {
        context.dispatch('loader/stopLoading', 'information/create', { root: true })
      })
  },
  update (context, { id, information }) {
    context.dispatch('loader/startLoading', 'information/update', { root: true })
    return informationService.update(id, information)
      .then(response => {
        context.dispatch('information/findEventInformations', context.state.datatableParams, { root: true })
        context.dispatch('alert/success', `Mise à jour réussie de l'information ${response.data.label}.`, { root: true })
        return response.data
      })
      .catch(() => {
        context.dispatch('alert/error', `Echec de la mise à jour de l'information ${information.label}.`, { root: true })
      })
      .finally(() => {
        context.dispatch('loader/stopLoading', 'information/update', { root: true })
      })
  },
  findAllFiles (context, files) {
    context.commit('findAllFiles', files)
  },
  removeFile (context, { eventId, informationId, fileId }) {
    return informationService.removeFile(eventId, informationId, fileId)
      .then(() => {
        context.commit('removeFile', fileId)
        context.dispatch('alert/success', 'La pièce jointe a bien été supprimée', { root: true })
      })
      .catch(() => {
        context.dispatch('alert/error', 'La suppression de la pièce jointe a échoué.', { root: true })
      })
  },
  findFile (context, { fileId }) {
    context.commit('findFile', fileId)
  },
  findHistory (context, { eventId, informationId }) {
    return informationService.findHistory(eventId, informationId)
      .then(response => {
        context.commit('findHistory', response.data)
        return response
      })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
